import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { getEngineApi } from "@/lib/api";
import type { OrgProjectScope } from "@/lib/resource-context";
import type { ToolExecutionsFilters } from "../search";
import type { ToolExecutionDetail, ToolExecutionsPage } from "../types";
import { toolExecutionsQueryParams } from "../utils/query-params";
import { toolExecutionsKeys } from "./query-keys";

type ListParams = {
  scope: OrgProjectScope;
  filters: ToolExecutionsFilters;
  limit: number;
  offset: number;
};

export const toolExecutionsQueryOptions = ({
  scope,
  filters,
  limit,
  offset,
}: ListParams) =>
  queryOptions<ToolExecutionsPage>({
    queryKey: toolExecutionsKeys.list({ ...scope, filters, limit, offset }),
    queryFn: async () => {
      const { data } = await getEngineApi(scope).get<ToolExecutionsPage>(
        "/tool_executions",
        { params: { limit, offset, ...toolExecutionsQueryParams(filters) } }
      );
      return data;
    },
    placeholderData: keepPreviousData,
  });

type DetailParams = {
  scope: OrgProjectScope;
  id: string;
  includeInputs: boolean;
  includeOutputs: boolean;
};

export const toolExecutionDetailQueryOptions = ({
  scope,
  id,
  includeInputs,
  includeOutputs,
}: DetailParams) =>
  queryOptions<ToolExecutionDetail>({
    queryKey: toolExecutionsKeys.detail({
      ...scope,
      id,
      includeInputs,
      includeOutputs,
    }),
    queryFn: async () => {
      const { data } = await getEngineApi(scope).get<ToolExecutionDetail>(
        `/tool_executions/${encodeURIComponent(id)}`,
        {
          params: {
            include_inputs: includeInputs,
            include_outputs: includeOutputs,
          },
        }
      );
      return data;
    },
    enabled: id !== "",
    placeholderData: keepPreviousData,
    // A revealed payload must not linger in the client cache after the panel
    // that requested it is gone.
    gcTime: includeInputs || includeOutputs ? 0 : undefined,
    retry: false,
  });
